import { ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import type { ScreenDTOV2 } from '@/common/types/screen'
import screenServerData from '@/common/services/serverData/screenServerData'
import skillServerData from '@/common/services/serverData/skillServerData'
import { getScreenByCode } from '@/common/services/ScreenService'
import { useCaseStore } from '@/agent/stores/caseStore'
import * as ScreenHelpers from '@/common/helpers/screen'
import * as SkillHelpers from '@/common/helpers/skill'

export const useScreenStore = defineStore('screen', () => {
  const customerRecordScreen = ref<ScreenDTOV2 | undefined>()
  const treatmentScreen = ref<ScreenDTOV2 | null>(null)
  const companyRecordScreen = ref<ScreenDTOV2 | undefined>()

  const { currentSkill } = storeToRefs(useCaseStore())

  const setCustomerRecordScreen = async () => {
    if (customerRecordScreen.value) return

    const screens = await screenServerData.fetchV3()
    const screenCode = ScreenHelpers.getCustomerRecordScreen(screens).code
    customerRecordScreen.value = await getScreenByCode(screenCode)
  }

  const setCompanyRecordScreen = async (refetch: boolean = false) => {
    if (companyRecordScreen.value && !refetch) return

    const screens = await screenServerData.fetchV3()
    const companyScreen = ScreenHelpers.getCompanyRecordScreen(screens)
    if (!companyScreen) {
      throw new Error('Company screen not found')
    }
    companyRecordScreen.value = await getScreenByCode(companyScreen.code)
  }

  const getTreatmentScreen = async (currentSkillCode: string) => {
    const [screens, skills] = await Promise.all([screenServerData.fetchV3(), skillServerData.fetch()])
    const spaceCode = SkillHelpers.getSkillByCode(skills, currentSkillCode)!.spaceCode
    const screenCode = ScreenHelpers.getTreatmentScreen(screens, spaceCode)!.code

    return getScreenByCode(screenCode)
  }

  watch(currentSkill, async (value) => {
    if (value) {
      treatmentScreen.value = await getTreatmentScreen(value)
      return
    }
    treatmentScreen.value = null
  })

  return {
    setCustomerRecordScreen,
    customerRecordScreen,
    treatmentScreen,
    setCompanyRecordScreen,
    companyRecordScreen,
  }
})
